
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Upload, Search, ShoppingCart, Shirt, Package, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import MainLayout from "@/components/layout/MainLayout";
import { Badge } from "@/components/ui/badge";

const products = [
  {
    id: 1,
    name: "Classic White T-Shirt",
    description: "100% cotton premium t-shirt",
    price: 24.99,
    image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "tshirts",
    available: true
  },
  {
    id: 2,
    name: "Canvas Tote",
    description: "Durable canvas tote bag",
    price: 19.99,
    image: "https://images.unsplash.com/photo-1622560480605-d83c661cb1fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "totes",
    available: true
  },
  {
    id: 6,
    name: "Art Print",
    description: "Premium quality art print",
    price: 14.99,
    image: "https://images.unsplash.com/photo-1605721911519-3dfeb3be25e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "posters",
    available: true
  },
  {
    id: 3,
    name: "Black Hoodie",
    description: "Cozy cotton-blend hoodie",
    price: 39.99,
    image: "https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "hoodies",
    available: false
  },
  {
    id: 4,
    name: "Baby Onesie",
    description: "Soft cotton baby onesie",
    price: 16.99,
    image: "https://images.unsplash.com/photo-1522771930-78848d9293e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "baby",
    available: false
  },
  {
    id: 5,
    name: "Dog Bandana",
    description: "Stylish bandana for your pup",
    price: 12.99,
    image: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "dog",
    available: false
  },
  {
    id: 7,
    name: "Color T-Shirts",
    description: "100% cotton t-shirts in various colors",
    price: 24.99,
    image: "https://images.unsplash.com/photo-1562157873-818bc0726f68?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "tshirts",
    available: false
  },
  {
    id: 8,
    name: "Color Hoodies",
    description: "Cozy cotton-blend hoodies in various colors",
    price: 39.99,
    image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "hoodies",
    available: false
  }
];

const categories = [
  { id: "all", name: "All Products" },
  { id: "tshirts", name: "T-Shirts" },
  { id: "hoodies", name: "Hoodies" },
  { id: "totes", name: "Tote Bags" },
  { id: "baby", name: "Baby Clothes" },
  { id: "dog", name: "Dog Gear" },
  { id: "posters", name: "Posters & Prints" }
];

const Shop = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [cartCount, setCartCount] = useState(0);

  // Reorder products to put available ones first
  const orderedProducts = [...products].sort((a, b) => {
    if (a.available && !b.available) return -1;
    if (!a.available && b.available) return 1;
    return 0;
  });

  const filteredProducts = orderedProducts.filter(product => {
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addToCart = () => {
    setCartCount(prev => prev + 1);
  };

  // Get icon based on category
  const getCategoryIcon = (category: string) => {
    switch(category) {
      case "tshirts":
        return <Shirt className="h-4 w-4 mr-2" />;
      case "totes":
        return <Package className="h-4 w-4 mr-2" />;
      case "posters":
        return <ImageIcon className="h-4 w-4 mr-2" />;
      default:
        return null;
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-12 px-4">
        <div className="flex justify-between items-center mb-8">
          <motion.h1
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-4xl font-display font-bold"
          >
            MAD-T's Shop
          </motion.h1>
          <div className="relative">
            <Button variant="outline" className="relative">
              <ShoppingCart className="h-5 w-5" />
              <span className="ml-2">Cart</span>
              {cartCount > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-madts-pink">
                  {cartCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12">
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Search</h3>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input 
                    placeholder="Search products..." 
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              
              <h3 className="text-lg font-semibold mb-3">Categories</h3>
              <div className="space-y-2">
                {categories.map(category => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? "default" : "ghost"}
                    className={`w-full justify-start ${selectedCategory === category.id ? 'bg-madts-purple text-white' : ''}`}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {getCategoryIcon(category.id)}
                    {category.name}
                  </Button>
                ))}
              </div>

              <div className="mt-8 p-4 bg-madts-gray/50 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold mb-2">Custom Design</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Need something unique? Upload your own design or use our AI generator!
                </p>
                <Button 
                  className="w-full bg-gradient-to-r from-madts-blue to-madts-purple text-white"
                  size="sm"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Custom Design
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold mb-2">No products found</h3>
                <p className="text-gray-500">Try adjusting your search or category filters</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map((product) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    whileHover={{ y: -5 }}
                  >
                    <Card className="overflow-hidden h-full flex flex-col relative">
                      {!product.available && (
                        <div className="absolute top-0 right-0 z-10 m-2">
                          <Badge className="bg-madts-pink/90 px-3 py-1 text-white">
                            Coming Soon
                          </Badge>
                        </div>
                      )}
                      <AspectRatio ratio={4/3}>
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="object-cover w-full h-full rounded-t-lg"
                        />
                      </AspectRatio>
                      <CardHeader className="pb-2">
                        <CardTitle>{product.name}</CardTitle>
                        <CardDescription>{product.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="flex-grow">
                        <p className="text-xl font-semibold text-madts-purple">
                          ${product.price.toFixed(2)}
                        </p>
                      </CardContent>
                      <CardFooter>
                        <Button 
                          className={`w-full ${product.available ? 'bg-madts-purple hover:bg-madts-purple/90' : 'bg-gray-400 hover:bg-gray-500 cursor-not-allowed'}`}
                          onClick={product.available ? addToCart : undefined}
                          disabled={!product.available}
                        >
                          {product.available ? 'Add to Cart' : 'Coming Soon'}
                        </Button>
                      </CardFooter>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Shop;
